// src/api/companyApi.ts
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { Company } from "../interfaces/entities/company";
import { Collection } from "../enums/collections.enum";
import { readAllEntity, createEntity, readEntity, deleteEntity, updateEntity, readFilteredEntity } from "../services/crudService";
import { deleteStorageFolder } from "../services/storageService";
import { AppState } from "../interfaces/app";
import { User } from "../interfaces/entities/user";
import { UserRoles } from "../enums/userRoles.enum";
import { isOwnerOfResource } from "../hooks/useAuthenticatedUser";

export const companyApi = createApi({
  reducerPath: "companyApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: [Collection.Companies, Collection.Users, Collection.Branches],
  endpoints: (build) => ({
    getCompanies: build.query<Company[], void>({
      async queryFn() {
        try {
          const companies = await readAllEntity<Company>(Collection.Companies);
          return { data: companies || [] };
        } catch (error) {
          return {
            error: { status: 500, message: (error as Error).message },
          };
        }
      },
      providesTags: [{ type: Collection.Companies, id: "LIST" }],
    }),

    getCompanyById: build.query<Company | null, string>({
      async queryFn(companyId) {
        try {
          const result = await readEntity<Company>(Collection.Companies, companyId);
          return { data: result };
        } catch (error) {
          return {
            error: { status: 500, message: (error as Error).message },
          };
        }
      },
      providesTags: (result, error, id) => [
        { type: Collection.Companies, id },
      ],
    }),

    createCompany: build.mutation<Company, Omit<Company, "id">>({
      async queryFn(companyData: Company, { getState }) {
        const state = getState() as { auth: AppState };
        const loggedInUser = state.auth.user;

        if (loggedInUser?.role === UserRoles.admin) {
          try {
            const createdCompany = await createEntity<Company>(
              Collection.Companies,
              { ...companyData }
            );
            console.log('Company Created');
            return { data: createdCompany };

          } catch (error) {
            return {
              error: { status: 500, message: (error as Error).message },
            };

          }
        } else {
          return {
            error: { status: 403, message: 'You do not have permission to add a company' },
          };
        }
      },
      invalidatesTags: [{ type: Collection.Companies, id: "LIST" }],
    }),

    /**
     * Deletes a company with all its dependencies:
     * 1. Check if any users exist with this companyId
     * 2. Check if any branches exist with this companyId
     * 3. Delete all company storage files
     * 4. Delete the company itself
     */
    deleteCompanyWithDependencies: build.mutation<void, string>({
      async queryFn(companyId, { getState }) {
        const state = getState() as { auth: AppState };
        const loggedInUser = state.auth.user;

        if (loggedInUser?.role === UserRoles.admin) {
          try {

            // Step 1: Check if any users exist with this companyId
            const users = await readFilteredEntity(Collection.Users, [
              { field: "companyId", operator: "==", value: companyId }
            ]);

            if (users && users.length > 0) {
              return {
                error: {
                  status: 400,
                  message: `Cannot delete company. ${users.length} user(s) exist for this company. Please delete all users first.`
                },
              };
            }

            // Step 2: Check if any branches exist with this companyId
            const branches = await readFilteredEntity(Collection.Branches, [
              { field: "companyId", operator: "==", value: companyId }
            ]);

            if (branches && branches.length > 0) {
              return {
                error: {
                  status: 400,
                  message: `Cannot delete company. ${branches.length} branch(es) exist for this company. Please delete all branches first.`
                },
              };
            }

            // Step 3: Delete company storage folder
            await deleteStorageFolder(`${Collection.Companies}/${companyId}`);

            // Step 4: Delete the company itself
            await deleteEntity(Collection.Companies, companyId);
            console.log(`[companyApi] Deleted company ${companyId}`);

            return { data: undefined };
          } catch (error) {
            console.error('[companyApi] Error in cascade delete:', error);
            return {
              error: { status: 500, message: (error as Error).message },
            };
          }
        }
        else {
          return {
            error: { status: 403, message: 'You do not have permission to delete a company' },
          };
        }
      },
      invalidatesTags: [
        { type: Collection.Companies, id: "LIST" },
        { type: Collection.Users, id: "LIST" },
        { type: Collection.Branches, id: "LIST" },
      ],
    }),

    updateCompany: build.mutation<void, { companyId: string; data: Partial<Company> }>({
      async queryFn({ companyId, data }, { getState }) {
        const state = getState() as { auth: { user: User | null } };
        const loggedInUser = state.auth?.user;

        if (isOwnerOfResource(companyId, loggedInUser) || loggedInUser?.role === UserRoles.admin) {
          try {
            await updateEntity(Collection.Companies, companyId, data);
            return { data: undefined };
          } catch (error) {
            return {
              error: { status: 500, message: (error as Error).message },
            };
          }
        } else {
          return {
            error: { status: 403, message: 'You do not have permission to update a company' },
          };
        }
      },

      invalidatesTags: (result, error, { companyId: id }) => [
        { type: Collection.Companies, id },
        { type: Collection.Companies, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetCompaniesQuery,
  useGetCompanyByIdQuery,
  useCreateCompanyMutation,
  // useDeleteCompanyMutation,
  useDeleteCompanyWithDependenciesMutation,
  useUpdateCompanyMutation,
} = companyApi;
